import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Loader2, PhoneCall, Mic, Sparkles } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Public page a patient opens from a WhatsApp link.
 * Loads the ElevenLabs ConvAI web widget and auto-connects to the HearClear agent.
 * No auth required.
 */
export default function TalkPage() {
  const { token } = useParams();
  const [session, setSession] = useState(null);
  const [error, setError] = useState(null);
  const [widgetReady, setWidgetReady] = useState(false);
  const widgetRef = useRef(null);

  // 1. Load session + agent id
  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/convai/session/${token}`);
        setSession(r.data);
        axios.post(`${API}/convai/session/${token}/opened`).catch(() => {});
      } catch (e) {
        setError(e?.response?.data?.detail || 'Link invalid or expired');
      }
    })();
  }, [token]);

  // 2. Inject the ElevenLabs widget script (once)
  useEffect(() => {
    if (!session?.agent_id) return;
    const existing = document.querySelector('script[data-elabs-convai]');
    if (existing) { setWidgetReady(true); return; }
    const s = document.createElement('script');
    s.src = 'https://unpkg.com/@elevenlabs/convai-widget-embed';
    s.async = true;
    s.type = 'text/javascript';
    s.setAttribute('data-elabs-convai', 'true');
    s.onload = () => setWidgetReady(true);
    document.body.appendChild(s);
  }, [session]);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-rose-50 to-orange-50 p-6">
        <div className="max-w-md text-center bg-white rounded-2xl shadow-lg p-8">
          <div className="w-14 h-14 rounded-full bg-rose-100 mx-auto flex items-center justify-center mb-3">
            <PhoneCall className="w-6 h-6 text-rose-600" />
          </div>
          <h2 className="font-bold text-stone-900 text-lg">This link isn't available</h2>
          <p className="text-sm text-stone-600 mt-2">{error}</p>
          <p className="text-xs text-stone-400 mt-4">
            Please contact HearClear on +91 98884 38843.
          </p>
        </div>
      </div>
    );
  }

  if (!session) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-rose-50 to-orange-50">
        <Loader2 className="w-6 h-6 animate-spin text-rose-600" />
      </div>
    );
  }

  const firstName = (session.patient_name || 'there').split(' ')[0];

  return (
    <div className="min-h-screen bg-gradient-to-br from-rose-50 to-orange-50 flex flex-col items-center justify-center p-6">
      <div className="max-w-lg w-full bg-white rounded-3xl shadow-2xl overflow-hidden">
        <div className="bg-gradient-to-br from-rose-600 to-orange-500 px-6 py-7 text-white text-center">
          <div className="inline-flex w-16 h-16 rounded-full bg-white/20 backdrop-blur items-center justify-center mb-2">
            <Sparkles className="w-7 h-7" />
          </div>
          <h1 className="font-heading text-xl font-bold">Hi {firstName} 👋</h1>
          <p className="text-sm text-white/90 mt-1">Priya from HearClear is ready to talk.</p>
        </div>

        <div className="px-6 py-6 text-center">
          <p className="text-sm text-stone-700 leading-relaxed">
            Tap the microphone button below to start a <b>live voice chat</b> — just like a phone call,
            but no number needed.
          </p>
          <p className="text-[11px] text-stone-500 mt-2">
            Works best with headphones · uses your phone's internet
          </p>

          <div className="mt-6 flex items-center justify-center min-h-[180px]">
            {!widgetReady ? (
              <div className="flex flex-col items-center gap-2 text-stone-400">
                <Loader2 className="w-6 h-6 animate-spin" />
                <span className="text-xs">Loading voice assistant…</span>
              </div>
            ) : (
              <div ref={widgetRef} className="w-full flex justify-center">
                {/* ElevenLabs ConvAI web component */}
                <elevenlabs-convai agent-id={session.agent_id} />
              </div>
            )}
          </div>

          <p className="text-[10px] text-stone-400 mt-6">
            Powered by HearClear · Private &amp; secure · <a href="https://hearclear.in" className="underline">hearclear.in</a>
          </p>
        </div>
      </div>

      <p className="text-[10px] text-stone-400 mt-4 flex items-center gap-1">
        <Mic className="w-2.5 h-2.5" /> Microphone access required
      </p>
    </div>
  );
}
